import { useState, useEffect } from "react";
import {
  getUserData,
  sendEmailOtp,
  getProfileById,
  buildUserData,
  isPlaceholderUsername,
  isUsernameTaken,
  isValidUsername,
  saveUsername,
  USERNAME_PENDING_PREFIX,
} from "../utils/authUtils";
import styles from "../styles/Login.module.css";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "./context/user/UserContext";
import { useContext } from "react";
import { showSuccess, showError, showWarning } from "../utils/toastUtils";
import { EULA_URL } from "../utils/legal";
import OtpVerification from "./OtpVerification";

const Login = () => {
  const { setUserData, isLoading: contextLoading } = useContext(UserContext);
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("email");
  const [pendingSession, setPendingSession] = useState(null);
  const [username, setUsername] = useState("");
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const userData = getUserData();

    if (userData && !contextLoading) {
      // Don't set userData here, as that's already handled by UserContext
      // Just navigate to the appropriate page
      if (userData.user.isAdmin || userData.user.isManager) {
        navigate("/admin"); // Direct them to admin panel
      } else {
        navigate("/account"); // Direct regular users to their account page
      }
    }
  }, [contextLoading, navigate]);

  const finishLogin = (session, profile) => {
    const data = buildUserData(session, profile);
    setUserData(data);

    if (data.user.isAdmin || data.user.isManager) {
      showSuccess(`Welcome back, ${data.user.username}!`);
      navigate("/admin");
    } else {
      showSuccess(`Welcome back, ${data.user.username}!`);
      navigate("/account");
    }
  };

  const validateEmail = () => {
    if (!email) {
      setError(true);
      setErrorMessage("Email is required");
      showWarning("Email is required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(true);
      setErrorMessage("Please enter a valid email address");
      showWarning("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const handleSendCode = async () => {
    if (!validateEmail()) return;

    setLoading(true);
    setError(false);

    try {
      await sendEmailOtp(email.trim().toLowerCase(), {
        shouldCreateUser: false,
      });
      setStep("otp");
      showSuccess("Code sent! Check your email.");
    } catch (err) {
      setError(true);
      setErrorMessage(err.message || "Could not send code. Please try again.");
      showError(err.message || "Could not send code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerified = async (data) => {
    const session = data?.session;
    if (!session?.user) {
      showError("Could not sign you in. Please request a new code.");
      return;
    }

    try {
      const profile = await getProfileById(session.user.id);

      // Never keep an auto-generated username: ask the user to choose one.
      if (isPlaceholderUsername(profile.username, profile.email)) {
        setPendingSession(session);
        setUsername("");
        setStep("username");
        return;
      }

      finishLogin(session, profile);
    } catch (err) {
      showError(err.message || "Could not load your profile. Please try again.");
    }
  };

  const handleSaveUsername = async () => {
    const trimmed = username.trim().toLowerCase();

    if (!isValidUsername(trimmed)) {
      showWarning(
        "Username must be 3-20 characters and contain only letters, numbers, or underscores.",
      );
      return;
    }

    if (trimmed.startsWith(USERNAME_PENDING_PREFIX)) {
      showWarning("This username prefix is reserved. Please choose another.");
      return;
    }

    setUsernameLoading(true);
    try {
      const taken = await isUsernameTaken(trimmed, pendingSession?.user?.id);
      if (taken) {
        showWarning("That username is already taken. Please choose another.");
        return;
      }

      const profile = await saveUsername(pendingSession, trimmed);
      finishLogin(pendingSession, profile);
    } catch (err) {
      showError(err.message || "Could not save your username. Please try again.");
    } finally {
      setUsernameLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendCode();
    }
  };

  if (step === "otp") {
    return (
      <OtpVerification
        email={email.trim().toLowerCase()}
        shouldCreateUser={false}
        onVerified={handleVerified}
        onBack={() => setStep("email")}
      />
    );
  }

  if (step === "username") {
    return (
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <h1>Choose Your Username</h1>
          <p className={styles.instruction}>
            Your account does not have a username yet. Pick one to continue.
          </p>
          <input
            placeholder="Username"
            className={styles.input}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSaveUsername()}
            autoFocus
          />
          <button
            onClick={handleSaveUsername}
            className={styles.button}
            disabled={usernameLoading}
          >
            {usernameLoading ? "Saving..." : "Continue"}
          </button>
          <div className={styles.linkContainer}>
            <p className={styles.switchLink}>
              Wrong account?{" "}
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => setStep("email")}
              >
                Start over
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <h1>Login</h1>
        <p className={styles.instruction}>
          Enter your email and we'll send you a 6-digit sign-in code. No
          password needed.
        </p>
        <input
          placeholder="Email"
          className={styles.input}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          type="email"
          autoFocus
        />
        <button
          onClick={handleSendCode}
          className={styles.button}
          disabled={loading}
        >
          {loading ? "Sending..." : "Send Code"}
        </button>
        {error && <span className={styles.error}>{errorMessage}</span>}
        <div className={styles.linkContainer}>
          <p className={styles.switchLink}>
            Don't have an account? <Link to="/signup">Sign Up</Link>
          </p>
        </div>
        <p className={styles.legalNotice}>
          By logging in, I AGREE to the{" "}
          <Link to="/terms" target="_blank">
            Terms of Service
          </Link>
          ,{" "}
          <Link to="/terms" target="_blank">
            Privacy Policy
          </Link>
          , and the{" "}
          <a href={EULA_URL} target="_blank" rel="noopener noreferrer">
            End User License Agreement (EULA)
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default Login;
